import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * ScrollToTop — reset the window scroll when navigating to a new page.
 *
 * Rob Morris, 2026-09-15: "in the onboarding portal if I click on onboarders it
 * always takes me to the bottom of the page instead of the top of that page."
 *
 * react-router's BrowserRouter (main.tsx) does no scroll management at all: a
 * client-side navigation swaps the component tree but leaves the document
 * scrolled exactly where it was. Land on a long page (Onboarders is the worst
 * offender — a full table of every rep) from a page you had scrolled down, and
 * you arrive mid-table or at the bottom, which reads as the page being broken.
 *
 * Not Onboarders-specific on purpose. The cause is router-level, so the fix is
 * too — every page in the app has the same behaviour, Onboarders is simply the
 * one long enough for it to be obvious.
 *
 * Deliberately keyed on `pathname` ONLY, not on `search`:
 *   - A real page change (Contracts -> Onboarders) scrolls to the top. Correct.
 *   - A filter/search/page-size change only rewrites the query string, and the
 *     admin is reading the table when it happens. Yanking them to the top on
 *     every keystroke of the search box would be its own bug — and a worse one,
 *     since D79 just finished making that box usable.
 *
 * POP navigations are excluded so the browser's own scroll restoration still
 * works: pressing Back should return you to where you were in the list, not to
 * the top of it. (D79 made filter changes `replace`, so a Back out of a
 * filtered list is a genuine page-level POP, not a walk through filter states.)
 *
 * D92/D84 (2026-09-15) — ROOT CAUSE FOUND AND REMOVED, not worked around.
 *
 * Six previous fixes (961bef9, 4e77d50, 0a2d763, ba4c77a, 580cc93, 3d35df0,
 * f2d7b30, 33dc0c0, 141fd44 — see
 * .claude/trnkey-sprint-plan-D92-scroll-logout.md BUG 1) all failed because
 * `<main id="main-content">` (AppShell.module.css) was its own
 * `overflow-y:auto` scroll container inside a `height:100vh; overflow:hidden`
 * shell — so the WINDOW never scrolled at all, and `scrollRestoration =
 * 'manual'` below (which only ever governs the window) had nothing to
 * intercept. The browser restored the inner container's own prior offset on
 * every reload/back, invisibly, with no scroll event to catch.
 *
 * AppShell.module.css no longer makes `.main` scroll internally — the
 * document/window is the real scroll container now, the sidenav stays pinned
 * via `position:sticky` instead of the old fixed-height trap. That means this
 * component's job shrinks to what it always should have been: turn off the
 * browser's automatic restoration and call `window.scrollTo`. All of the
 * ResizeObserver/MutationObserver/hold-until-settled machinery that used to
 * live here existed ONLY to re-assert a scroll position against an inner
 * container that kept fighting back as rows rendered into it — with a real
 * window scroll there is nothing left to fight, so it has been deleted.
 * (`git show 580cc93:apps/web/src/components/ScrollToTop.tsx` has the old
 * version if a future regression needs the history.)
 */

/**
 * `history.scrollRestoration` defaults to "auto", so on a reload the browser
 * restores the previous scroll position itself, before React renders
 * anything, and react-router reports that reload as a POP (which the effects
 * below deliberately skip so Back/Forward still restores your place in a
 * list). Setting this to "manual" stops the browser from second-guessing us
 * on reload while leaving POP handling exactly as before.
 */
function useManualScrollRestoration(): void {
  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);
}

export default function ScrollToTop() {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();

  useManualScrollRestoration();

  // A real page change (not Back/Forward) goes to the top of the new page.
  useEffect(() => {
    if (navigationType === 'POP') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    // navigationType is deliberately not a dependency — it is read at the time
    // the pathname changes, and adding it would re-fire this effect when the
    // type changes without the route changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // D84 follow-up. Paging through a list does not change the path, only
  // `?page=`. The Onboarders list renders 50 rows a page (511 people, 11
  // pages), so without this: scroll to the bottom, click Next, and the new
  // page renders with the window still scrolled to the bottom.
  //
  // Deliberately watches ONLY the page parameter, not the whole query string.
  // D79 fixed the search box by making filter changes replace history instead
  // of pushing it; scrolling to the top on every filter keystroke would undo
  // that and be a worse bug than this one — the admin is reading the table
  // while they type. A page change is a different intent: you asked for the
  // next 50 rows, so you want to be at the start of them.
  const page = new URLSearchParams(search).get('page') ?? '1';
  useEffect(() => {
    if (navigationType === 'POP') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return null;
}
